import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { BsArrowLeft, BsCloudUpload, BsPersonFill } from "react-icons/bs";

import Sidebar from "../../components/sidebar/Sidebar";

import {
  serviceGetEmployeeInfo,
  serviceUpdateEmployeeInfo,
} from "../../api/serviceApi";

import {
  serviceDeletePhoto,
  serviceUploadPhoto,
} from "../../api/servicePhotoApi";

import { getImageUrl } from "../../utils/helper";

/*
 * Reuse the Add Employee styling so create/update
 * pages stay visually consistent.
 *
 * Adjust path if EditEmployee lives in another folder.
 */
import "./editemployee.css";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const INITIAL_FORM = {
  username: "",
  email: "",
  name: "",
  role: "",

  address: "",
  phone: "",
  employmentType: "",
  dob: "",

  imageUrl: "",
};

const ROLE_OPTIONS = [
  "ADMIN",
  "MANAGER",
  "TECHNICIAN",
  "FITTER",
  "DISTRIBUTOR",
];

// ============================================================
// HELPERS
// ============================================================

const isImageFile = (file) => {
  if (file?.type?.startsWith("image/")) {
    return true;
  }

  return /\.(jpg|jpeg|png|webp|heic|heif|avif)$/i.test(file?.name || "");
};

const toDateInput = (value) => {
  if (!value) {
    return "";
  }

  /*
   * Handles API values such as:
   * 1990-05-20
   * 1990-05-20T00:00:00Z
   */
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().slice(0, 10);
};

const makeProfileFilename = (email, id) => {
  const safeValue = String(email || id || "employee")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "_");

  return [
    safeValue,
    "profile",
    Date.now(),
    Math.random().toString(36).slice(2, 8),
  ].join("-");
};

/*
 * Only attempt storage deletion for photos managed by
 * your new photo server.
 *
 * This avoids trying to send old Firebase URLs to the
 * new image delete API during migration.
 */
const isManagedPhoto = (url) => {
  if (!url) {
    return false;
  }

  return String(url).startsWith("/photo/") || String(url).includes("/photo/");
};

// ============================================================
// COMPONENT
// ============================================================

const EditEmployee = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(INITIAL_FORM);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Newly selected replacement image.
  const [image, setImage] = useState(null);
  // Local preview for newly selected image.
  const [previewUrl, setPreviewUrl] = useState("");
  /*
   * Original image returned by API.
   * Kept separately so it can be cleaned up after
   * a successful replacement/removal.
   */
  const [originalImageUrl, setOriginalImageUrl] = useState("");
  // User explicitly clicked Remove Photo.
  const [removeExistingImage, setRemoveExistingImage] = useState(false);

  // ==========================================================
  // LOAD EMPLOYEE
  // ==========================================================

  useEffect(() => {
    if (!id) {
      return;
    }

    let cancelled = false;

    const loadEmployee = async () => {
      try {
        setLoading(true);

        const result = await serviceGetEmployeeInfo(id);

        if (cancelled) {
          return;
        }

        const employee =
          result?.user || result?.employee || result?.data || result;

        if (!employee || typeof employee !== "object") {
          throw new Error("Employee information was not found.");
        }

        const employeeImageUrl = employee.imageUrl || "";

        setForm({
          username: employee.username || "",

          email: employee.email || "",

          name: employee.name || "",

          role: employee.role || "",

          address: employee.address || "",

          phone: employee.phone || "",

          /*
           * Supports old Firebase field during migration
           * as well as your new API field.
           */
          employmentType:
            employee.employmentType || employee.employment_type || "",

          dob: toDateInput(employee.dob),

          imageUrl: employeeImageUrl,
        });

        setOriginalImageUrl(employeeImageUrl);

        setImage(null);
        setRemoveExistingImage(false);
      } catch (error) {
        console.error("Error loading employee:", error);

        if (!cancelled) {
          toast.error(error?.message || "Failed to load employee.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadEmployee();

    return () => {
      cancelled = true;
    };
  }, [id]);

  // ==========================================================
  // LOCAL IMAGE PREVIEW
  // ==========================================================

  useEffect(() => {
    if (!image) {
      setPreviewUrl("");
      return;
    }

    const objectUrl = URL.createObjectURL(image);

    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [image]);

  // ==========================================================
  // FORM
  // ==========================================================

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  // ==========================================================
  // PHOTO SELECTION
  // ==========================================================
  const handleImageChoose = (event) => {
    const file = event.target.files?.[0];

    // Allows selecting the same file again.
    event.target.value = "";

    if (!file) return;

    if (!isImageFile(file)) {
      toast.error("Please select a valid image file.");

      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error("Profile image must be 5MB or smaller.");
      return;
    }

    setImage(file);

    /*
     * Selecting another image cancels an earlier
     * explicit "Remove Photo" action.
     */
    setRemoveExistingImage(false);
  };

  // ==========================================================
  // REMOVE PHOTO
  // ==========================================================

  const handleRemovePhoto = () => {
    if (submitting) {
      return;
    }

    /*
     * Remove newly selected replacement first.
     */
    if (image) {
      setImage(null);

      return;
    }

    /*
     * Existing stored profile picture.
     */
    if (form.imageUrl) {
      setForm((current) => ({
        ...current,
        imageUrl: "",
      }));

      setRemoveExistingImage(true);
    }
  };

  // ==========================================================
  // UPLOAD PROFILE IMAGE
  // ==========================================================

  const uploadProfileImage = async () => {
    if (!image) {
      return "";
    }

    try {
      setUploadingPhoto(true);

      const result = await serviceUploadPhoto({
        file: image,
        folder: "tims/profilePictures",

        filename: makeProfileFilename(form.email, id),
      });

      if (!result?.url) {
        throw new Error("Profile image upload failed.");
      }

      return result.url;
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ==========================================================
  // UPDATE PROFILE
  // ==========================================================
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting || loading) {
      return;
    }

    if (!form.role) {
      toast.error("Please select an employee role.");

      return;
    }

    /*
     * If a new photo uploads but updating the
     * employee fails, delete this file again.
     */
    let uploadedImageUrl = "";
    let employeeUpdated = false;

    try {
      setSubmitting(true);

      // ------------------------------------------------------
      // NEW PROFILE PHOTO
      // ------------------------------------------------------

      if (image) {
        uploadedImageUrl = await uploadProfileImage();
      }

      // ------------------------------------------------------
      // BUILD PAYLOAD
      // ------------------------------------------------------

      const payload = {
        id: id,
        username: form.username.trim(),
        /*
         * Email and role remains disabled in the UI but is
         * included in case the API DTO expects it.
         */
        email: form.email.trim(),
        role: form.role,

        name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        employmentType: form.employmentType.trim(),
        dob: form.dob || "",
      };

      /*
       * Only send imageUrl if something actually
       * changed with the image.
       */
      if (uploadedImageUrl) {
        payload.imageUrl = uploadedImageUrl;
      } else if (removeExistingImage) {
        payload.imageUrl = "";
      }

      // ------------------------------------------------------
      // UPDATE THROUGH API
      // ------------------------------------------------------

      await serviceUpdateEmployeeInfo(payload);

      employeeUpdated = true;

      // ------------------------------------------------------
      // CLEAN UP PREVIOUS PROFILE PHOTO
      // ------------------------------------------------------

      const oldPhotoNeedsDeleting =
        originalImageUrl &&
        (uploadedImageUrl || removeExistingImage) &&
        originalImageUrl !== uploadedImageUrl;

      if (oldPhotoNeedsDeleting && isManagedPhoto(originalImageUrl)) {
        try {
          await serviceDeletePhoto(originalImageUrl);
        } catch (cleanupError) {
          /*
           * Employee update already succeeded.
           * Don't turn successful employee update
           * into an error just because old file
           * cleanup failed.
           */
          console.error("Failed deleting old profile photo:", cleanupError);

          toast.warn(
            "Employee updated, but the previous profile photo could not be cleaned up.",
          );
        }
      }

      toast.success("Employee updated successfully.");

      navigate(`/employees/view/${id}`, { replace: true });
    } catch (error) {
      console.error("Error updating employee:", error);

      /*
       * A newly uploaded image isn't owned by the
       * employee unless the actual employee update
       * succeeded.
       */
      if (uploadedImageUrl && !employeeUpdated) {
        try {
          await serviceDeletePhoto(uploadedImageUrl);
        } catch (cleanupError) {
          console.error(
            "Failed cleaning up uploaded profile image:",
            cleanupError,
          );
        }
      }

      toast.error(error?.message || "Failed to update employee.");
    } finally {
      setSubmitting(false);
      setUploadingPhoto(false);
    }
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div className="new-employee-page">
        <Sidebar />

        <main className="new-employee-content">
          <div className="new-employee-container">
            <div className="new-employee-state">
              <BsPersonFill />

              <strong>Loading employee...</strong>

              <span>Please wait while the employee information is loaded.</span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ==========================================================
  // CURRENT DISPLAY IMAGE
  // ==========================================================

  const currentImageUrl =
    previewUrl || (!removeExistingImage ? getImageUrl(form.imageUrl) : "");

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="new-employee-page">
      <Sidebar />

      <main className="new-employee-content">
        <div className="new-employee-container">
          {/* ================================================
              HEADER
          ================================================= */}

          <header className="new-employee-header">
            <div>
              <button
                type="button"
                className="new-employee-back"
                onClick={() => navigate(-1)}
                disabled={submitting}
              >
                <BsArrowLeft />
                Back
              </button>

              <h1>Update Employee</h1>

              <p>Update employee account and personal information.</p>
            </div>
          </header>

          {/* ================================================
              CARD
          ================================================= */}

          <form className="new-employee-card" onSubmit={handleSubmit}>
            {/* ==============================================
                PROFILE
            =============================================== */}

            <aside className="new-employee-photo">
              <div className="employee-photo-preview">
                {currentImageUrl ? (
                  <img
                    src={currentImageUrl}
                    alt={form.name || form.username || "Employee"}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <BsPersonFill />
                )}
              </div>

              <div className="employee-photo-info">
                <strong>Profile Photo</strong>

                <span>Update the employee's profile image.</span>
              </div>

              <label
                htmlFor="employee-photo"
                className={`employee-upload-btn ${
                  submitting ? "disabled" : ""
                }`}
              >
                <BsCloudUpload />

                {currentImageUrl ? "Change Photo" : "Upload Photo"}
              </label>

              <input
                id="employee-photo"
                type="file"
                accept="image/*"
                className="employee-file-input"
                disabled={submitting}
                onChange={handleImageChoose}
              />

              {(image || form.imageUrl) && (
                <div className="employee-selected-file">
                  <span>{image ? image.name : "Current profile photo"}</span>

                  <button
                    type="button"
                    disabled={submitting}
                    onClick={handleRemovePhoto}
                  >
                    Remove
                  </button>
                </div>
              )}

              {submitting && image && (
                <div className="employee-upload-progress">
                  <div className="employee-progress-header">
                    <span>
                      {uploadingPhoto ? "Uploading photo" : "Photo ready"}
                    </span>

                    <strong>{uploadingPhoto ? "..." : "✓"}</strong>
                  </div>
                </div>
              )}
            </aside>

            {/* ==============================================
                FORM
            =============================================== */}

            <div className="new-employee-form">
              {/* ACCOUNT */}

              <div className="new-employee-section-header">
                <h2>Account Details</h2>

                <p>Employee account and role information.</p>
              </div>

              <div className="employee-form-grid">
                {/* EMAIL */}

                <div className="employee-form-field">
                  <label htmlFor="email">Email</label>

                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={form.email}
                    disabled
                  />
                </div>

                {/* USERNAME */}

                <div className="employee-form-field">
                  <label htmlFor="username">Username</label>

                  <input
                    id="username"
                    type="text"
                    name="username"
                    value={form.username}
                    placeholder="Username"
                    disabled={submitting}
                    onChange={handleInputChange}
                  />
                </div>

                {/* ROLE */}

                <div className="employee-form-field">
                  <label htmlFor="role">
                    Role <span>*</span>
                  </label>

                  <select
                    id="role"
                    name="role"
                    value={form.role}
                    disabled={true}
                    required
                    onChange={handleInputChange}
                  >
                    <option value="" disabled>
                      Select role...
                    </option>

                    {ROLE_OPTIONS.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>

                {/* EMPLOYMENT */}

                <div className="employee-form-field">
                  <label htmlFor="employmentType">Employment Type</label>

                  <input
                    id="employmentType"
                    type="text"
                    name="employmentType"
                    value={form.employmentType}
                    placeholder="Full Time, Part Time..."
                    disabled={submitting}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="new-employee-divider" />

              {/* PERSONAL */}

              <div className="new-employee-section-header">
                <h2>Personal Details</h2>

                <p>Employee contact and personal information.</p>
              </div>

              <div className="employee-form-grid">
                {/* NAME */}

                <div className="employee-form-field">
                  <label htmlFor="name">Name</label>

                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={form.name}
                    placeholder="Employee name"
                    disabled={submitting}
                    onChange={handleInputChange}
                  />
                </div>

                {/* PHONE */}

                <div className="employee-form-field">
                  <label htmlFor="phone">Phone</label>

                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    value={form.phone}
                    placeholder="Phone number"
                    disabled={submitting}
                    onChange={handleInputChange}
                  />
                </div>

                {/* ADDRESS */}

                <div className="employee-form-field employee-full-width">
                  <label htmlFor="address">Address</label>

                  <input
                    id="address"
                    type="text"
                    name="address"
                    value={form.address}
                    placeholder="Employee address"
                    disabled={submitting}
                    onChange={handleInputChange}
                  />
                </div>

                {/* DOB */}

                <div className="employee-form-field">
                  <label htmlFor="dob">Date of Birth</label>

                  <input
                    id="dob"
                    type="date"
                    name="dob"
                    value={form.dob}
                    disabled={submitting}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* ============================================
                  ACTIONS
              ============================================= */}

              <div className="new-employee-actions">
                <button
                  type="button"
                  className="new-employee-btn ghost"
                  disabled={submitting}
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="new-employee-btn primary"
                  disabled={submitting}
                >
                  {submitting
                    ? uploadingPhoto
                      ? "Uploading Photo..."
                      : "Saving Changes..."
                    : "Save Changes"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default EditEmployee;
